import React, { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePatientSearch } from '@/hooks/usePatientSearch';
import { format, addDays, subDays } from 'date-fns';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, User,
  CheckCircle2, Loader2, Search, UserCheck, X, AlertCircle, Info,
  CreditCard, Printer,
} from 'lucide-react';
import { api, opdApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { useBranding } from '@/shared/hooks/useBranding';
import PrintableInvoice, { InvoicePaperSize } from '@/features/billing/components/PrintableInvoice';
import PatientQuickViewModal from '@/features/patients/components/PatientQuickViewModal';
import ListSearchInput from '@/components/ui/ListSearchInput';
import { clsx } from 'clsx';

const STATUS_STYLE: Record<string, string> = {
  scheduled:  'bg-blue-100 text-blue-700',
  confirmed:  'bg-green-100 text-green-700',
  arrived:    'bg-yellow-100 text-yellow-700',
  completed:  'bg-gray-100 text-gray-600',
  cancelled:  'bg-red-100 text-red-500',
  no_show:    'bg-orange-100 text-orange-600',
};

const APPT_TYPE_LABEL: Record<string, string> = {
  new:       'New Patient',
  follow_up: 'Follow-Up',
  procedure: 'Procedure',
};

function Toast({ msg, type, onClose }: { msg: string; type: 'error' | 'success' | 'warning'; onClose: () => void }) {
  const styles = {
    error:   'bg-red-50 border-red-200 text-red-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
  };
  return (
    <div className={clsx(
      'fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3',
      'px-5 py-3 rounded-xl shadow-xl text-sm font-medium border max-w-md w-full',
      styles[type],
    )}>
      {type === 'error' ? <AlertCircle size={16}/> : type === 'warning' ? <AlertCircle size={16}/> : <Info size={16}/>}
      <span className="flex-1">{msg}</span>
      <button onClick={onClose} className="text-current opacity-60 hover:opacity-100"><X size={14}/></button>
    </div>
  );
}

interface WalkinInvoice {
  id:               string;
  invoice_number:   string;
  consultation_fee: number;
  appointment_no:   string;
  token_number:     number;
  encounter_no:     string;
}

function fmtRupee(n: number) {
  return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function BookModal({
  onClose,
  onWalkinSuccess,
}: {
  onClose: () => void;
  onWalkinSuccess?: (data: { appointment_no: string; token_number: number; encounter_no: string }) => void;
}) {
  const qc           = useQueryClient();
  const { data: branding } = useBranding();
  const hospitalName        = useAuthStore(s => s.user?.hospitalName ?? '');

  // ── Booking state ──────────────────────────────────────────────────
  const [view,            setView]           = useState<'BOOKING' | 'PAYMENT' | 'SUCCESS'>('BOOKING');
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [doctorId,        setDoctorId]        = useState('');
  const [date,            setDate]            = useState(format(new Date(), 'yyyy-MM-dd'));
  const [slot,            setSlot]            = useState('');
  const [apptType,        setApptType]        = useState('new');
  const [source,          setSource]          = useState('reception');
  const [notes,           setNotes]           = useState('');

  // ── Walk-in payment state ──────────────────────────────────────────
  const [walkinInvoice, setWalkinInvoice] = useState<WalkinInvoice | null>(null);
  const [payAmount,     setPayAmount]     = useState('');
  const [payMode,       setPayMode]       = useState('cash');
  const [payRef,        setPayRef]        = useState('');
  const [payError,      setPayError]      = useState('');
  const [printTarget,   setPrintTarget]   = useState<any>(null);
  const [printSize,     setPrintSize]     = useState<InvoicePaperSize>('A4');
  const payModeRef = useRef<HTMLSelectElement>(null);

  const isWalkin = source === 'walkin';

  const {
    query:       patientSearch,
    setQuery:    setPatientSearch,
    results:     patients,
    isSearching: patientsFetching,
    clear:       clearPatientSearch,
  } = usePatientSearch(300, 2);

  const { data: doctorsData } = useQuery({
    queryKey: ['opd-doctors'],
    queryFn:  () => opdApi.doctors().then(r => r.data),
  });

  const { data: slotsData } = useQuery({
    queryKey: ['slots', doctorId, date],
    queryFn:  () =>
      doctorId && date
        ? opdApi.slots(doctorId, date).then(r => r.data)
        : Promise.resolve({ slots: [] }),
    enabled: !!(doctorId && date && !isWalkin),
  });

  const bookMutation = useMutation({
    mutationFn: (payload: any) => api.post('/appointments', payload).then(r => r.data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      if (res.walkin) {
        const fee = Number(res.consultation_fee ?? 0);
        setWalkinInvoice({
          id:               res.invoice_id,
          invoice_number:   res.invoice_number,
          consultation_fee: fee,
          appointment_no:   res.appointment_no,
          token_number:     res.token_number,
          encounter_no:     res.encounter_no,
        });
        setPayAmount(String(fee));
        setView('PAYMENT');
        requestAnimationFrame(() => { if (payModeRef.current) payModeRef.current.focus(); });
      } else {
        onClose();
      }
    },
  });

  const payMutForWalkin = useMutation({
    mutationFn: () => api.post('/billing/payments', {
      invoice_id:       walkinInvoice!.id,
      amount:           Number(payAmount),
      payment_mode:     payMode,
      reference_number: payRef || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['billing-dashboard'] });
      setView('SUCCESS');
    },
    onError: (e: any) => setPayError(e.response?.data?.error ?? 'Payment failed'),
  });

  const doctors: any[] = doctorsData?.doctors ?? [];
  const slots:   any[] = (slotsData?.slots ?? []).filter((s: any) => s.available);

  function handleBook() {
    if (!selectedPatient || !doctorId) return;
    if (!isWalkin && !slot) return;
    bookMutation.mutate({
      patient_id:       selectedPatient.id,
      doctor_id:        doctorId,
      appointment_date: isWalkin ? undefined : date,
      slot_start:       isWalkin ? undefined : slot,
      appointment_type: apptType,
      booking_source:   source,
      notes:            notes || undefined,
    });
  }

  function handleSkipPayment() {
    if (walkinInvoice && onWalkinSuccess) {
      onWalkinSuccess({
        appointment_no: walkinInvoice.appointment_no,
        token_number:   walkinInvoice.token_number,
        encounter_no:   walkinInvoice.encounter_no,
      });
    }
    onClose();
  }

  function handleDone() {
    if (walkinInvoice && onWalkinSuccess) {
      onWalkinSuccess({
        appointment_no: walkinInvoice.appointment_no,
        token_number:   walkinInvoice.token_number,
        encounter_no:   walkinInvoice.encounter_no,
      });
    }
    onClose();
  }

  async function handlePrintWalkinReceipt() {
    if (!walkinInvoice) return;
    try {
      const res = await api.get(`/billing/invoices/${walkinInvoice.id}`);
      setPrintTarget(res.data);
    } catch { /* fail silently */ }
  }

  const headerTitle =
    view === 'PAYMENT' ? 'Collect Consultation Fee' :
    view === 'SUCCESS' ? 'Payment Complete' :
    isWalkin           ? 'Walk-in Check-In' : 'Book Appointment';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <h2 className="font-bold text-navy text-lg flex items-center gap-2">
            {view === 'PAYMENT'
              ? <CreditCard size={16}/>
              : view === 'SUCCESS'
                ? <CheckCircle2 size={16} className="text-green-500"/>
                : <Plus size={16}/>}
            {headerTitle}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* BOOKING VIEW */}
          {view === 'BOOKING' && (
            <div className="p-6 space-y-4">

              {/* Walk-in info banner */}
              {isWalkin && (
                <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5">
                  <Info size={13} className="mt-0.5 flex-none"/>
                  <span>
                    Patient will be added to today's OPD queue immediately with a token number and a draft invoice.
                    No slot selection needed.
                  </span>
                </div>
              )}

              {/* Patient search */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Patient</label>
                {selectedPatient ? (
                  <div className="flex items-center justify-between gap-2 text-sm text-green-700 bg-green-50 border border-green-100 rounded-xl px-3 py-2">
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 size={13}/>
                      <strong>{selectedPatient.name}</strong>
                      <span className="text-xs text-green-600 font-mono">— {selectedPatient.patient_uid}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => { setSelectedPatient(null); clearPatientSearch(); }}
                      className="text-xs text-green-600 underline hover:text-green-800"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                    {patientsFetching && (
                      <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"/>
                    )}
                    <input
                      autoFocus
                      value={patientSearch}
                      onChange={e => setPatientSearch(e.target.value)}
                      placeholder="Search by name or UHID…"
                      className="w-full pl-8 pr-8 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-navy/20 outline-none"
                    />
                    {patients.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 z-[200] bg-white border border-gray-100 rounded-xl shadow-lg max-h-48 overflow-y-auto divide-y divide-gray-50">
                        {patients.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onMouseDown={e => {
                              e.preventDefault();
                              setSelectedPatient(p);
                              clearPatientSearch();
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-navy/5 transition"
                          >
                            <span className="font-medium text-navy">{p.name}</span>
                            <span className="text-xs text-gray-400 ml-2 font-mono">{p.patient_uid}</span>
                            {p.age_years && (
                              <span className="text-xs text-gray-400 ml-1">· {p.age_years}y {p.gender}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {patientSearch.length >= 2 && !patientsFetching && patients.length === 0 && (
                      <p className="absolute left-0 right-0 top-full mt-1 z-[200] bg-white border border-gray-100 rounded-xl shadow-sm text-xs text-gray-400 text-center py-3">
                        No matching patient found
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Doctor */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Doctor</label>
                <select value={doctorId} onChange={e => setDoctorId(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none">
                  <option value="">Select doctor...</option>
                  {doctors.map((d: any) => (
                    <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` (${d.specialization})` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Date and Slots — hidden for walk-ins */}
              {!isWalkin && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Date</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                      min={format(new Date(), 'yyyy-MM-dd')}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none"/>
                  </div>
                  {doctorId && date && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-2">
                        Available Slots {slots.length === 0 ? '(none available)' : `(${slots.length})`}
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {slots.slice(0, 16).map((s: any) => (
                          <button
                            key={s.start}
                            onClick={() => setSlot(s.start)}
                            className={clsx(
                              'px-2 py-1.5 rounded-lg text-xs font-medium border transition',
                              slot === s.start
                                ? 'bg-navy text-white border-navy'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-navy'
                            )}
                          >
                            {s.start}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Type & Source */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Type</label>
                  <select value={apptType} onChange={e => setApptType(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none">
                    <option value="new">New Patient</option>
                    <option value="follow_up">Follow-Up</option>
                    <option value="procedure">Procedure</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Booked Via</label>
                  <select value={source} onChange={e => { setSource(e.target.value); setSlot(''); }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none">
                    <option value="reception">Reception</option>
                    <option value="phone">Phone</option>
                    <option value="online">Online</option>
                    <option value="walkin">Walk-in</option>
                  </select>
                </div>
              </div>

              {/* Notes / chief complaint */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  {isWalkin ? 'Chief Complaint (optional)' : 'Notes (optional)'}
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  placeholder={isWalkin ? 'Reason for visit…' : 'Any notes for the appointment…'}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none resize-none"/>
              </div>
            </div>
          )}

          {/* PAYMENT VIEW */}
          {view === 'PAYMENT' && walkinInvoice && (
            <div className="p-6 space-y-4">

              {/* Walk-in summary card */}
              <div className="bg-navy/5 border border-navy/10 rounded-xl p-4">
                <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Walk-in Registered</div>
                <div className="font-semibold text-navy">{selectedPatient?.name}</div>
                <div className="text-xs text-gray-400 mt-0.5 font-mono">
                  {walkinInvoice.appointment_no}
                  {' · '}Token T-{String(walkinInvoice.token_number).padStart(2,'0')}
                  {' · '}{walkinInvoice.encounter_no}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Invoice {walkinInvoice.invoice_number} · Consultation Fee
                </div>
              </div>

              {/* Payment form */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1.5">Amount (₹) *</label>
                  <input
                    type="number"
                    value={payAmount}
                    onChange={e => setPayAmount(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1.5">Mode *</label>
                  <select
                    ref={payModeRef}
                    value={payMode}
                    onChange={e => setPayMode(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
                  >
                    {['cash','upi','card','neft','cheque','dd','wallet'].map(m => (
                      <option key={m} value={m}>{m.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1.5">
                    Reference # (optional)
                  </label>
                  <input
                    value={payRef}
                    onChange={e => setPayRef(e.target.value)}
                    placeholder="UPI transaction ID, cheque number…"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
                  />
                </div>
              </div>

              {payError && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-lg px-3 py-2">
                  <AlertCircle size={14}/> {payError}
                </div>
              )}
            </div>
          )}

          {/* SUCCESS VIEW */}
          {view === 'SUCCESS' && walkinInvoice && (
            <div className="p-6 py-12 text-center space-y-3">
              <CheckCircle2 size={48} className="text-green-500 mx-auto"/>
              <div>
                <p className="text-xl font-bold text-navy">Payment Recorded</p>
                <p className="text-sm text-gray-500 mt-1">
                  {fmtRupee(Number(payAmount))} received &mdash; {walkinInvoice.invoice_number}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{selectedPatient?.name}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {view === 'BOOKING' && (
          <div className="px-6 pb-5 pt-2 flex gap-3 justify-end flex-shrink-0 border-t border-gray-100">
            <button onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleBook}
              disabled={bookMutation.isPending || !selectedPatient || !doctorId || (!isWalkin && !slot)}
              className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy/90 disabled:opacity-40"
            >
              {bookMutation.isPending
                ? <Loader2 size={14} className="animate-spin"/>
                : isWalkin ? <UserCheck size={14}/> : <Plus size={14}/>}
              {isWalkin ? 'Check-In & Add to Queue' : 'Book Appointment'}
            </button>
          </div>
        )}
        {view === 'PAYMENT' && (
          <div className="px-6 pb-5 pt-4 flex gap-3 flex-shrink-0 border-t border-gray-100">
            <button
              onClick={handleSkipPayment}
              disabled={payMutForWalkin.isPending}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              Skip for Now
            </button>
            <button
              onClick={() => { setPayError(''); payMutForWalkin.mutate(); }}
              disabled={!payAmount || Number(payAmount) <= 0 || payMutForWalkin.isPending}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-40"
            >
              {payMutForWalkin.isPending
                ? <><Loader2 size={14} className="animate-spin"/> Recording…</>
                : <><CreditCard size={14}/> Record Payment</>}
            </button>
          </div>
        )}
        {view === 'SUCCESS' && (
          <div className="px-6 pb-5 pt-4 flex gap-3 flex-shrink-0 border-t border-gray-100">
            <button
              onClick={handlePrintWalkinReceipt}
              disabled={!branding}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-navy text-navy text-sm font-semibold hover:bg-navy/5 disabled:opacity-40"
            >
              <Printer size={14}/> Print Receipt
            </button>
            <button
              onClick={handleDone}
              className="flex-1 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90"
            >
              Done
            </button>
          </div>
        )}

        {/* Print portal — same pattern as BillingPage */}
        {printTarget && branding && (
          <PrintableInvoice
            inv={printTarget}
            branding={branding}
            hospitalName={hospitalName}
            paperSize={printSize}
            onDone={() => setPrintTarget(null)}
          />
        )}
      </div>
    </div>
  );
}

export default function AppointmentsPage() {
  const qc = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [doctorFilter, setDoctorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [showBook, setShowBook]         = useState(false);
  const [quickViewId, setQuickViewId]   = useState<string | null>(null);
  const [toast, setToast]               = useState<{ msg: string; type: 'error' | 'success' | 'warning' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showToast(msg: string, type: 'error' | 'success' | 'warning' = 'success') {
    setToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }

  const { data, isLoading } = useQuery({
    queryKey: ['appointments', selectedDate, doctorFilter, statusFilter],
    queryFn: () =>
      api.get('/appointments', {
        params: {
          date:      selectedDate,
          doctor_id: doctorFilter || undefined,
          status:    statusFilter || undefined,
        },
      }).then(r => r.data),
    staleTime: 30_000,
  });

  const { data: doctorsData } = useQuery({
    queryKey: ['opd-doctors'],
    queryFn: () => opdApi.doctors().then(r => r.data),
    staleTime: 5 * 60_000,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/appointments/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });

  const checkInMutation = useMutation({
    mutationFn: (id: string) => api.post(`/appointments/${id}/check-in`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      showToast(`Patient checked in. Token T-${String(data.tokenNumber).padStart(2, '0')} generated.`, 'success');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error ?? 'Check-in failed. Please try again.';
      const type = err?.response?.status === 409 ? 'warning' : 'error';
      showToast(msg, type);
    },
  });

  const appointments: any[] = data?.appointments ?? [];

  const filteredAppointments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return appointments;
    return appointments.filter((a: any) =>
      (a.patient_name ?? '').toLowerCase().includes(q) ||
      (a.patient_uid  ?? '').toLowerCase().includes(q),
    );
  }, [appointments, searchQuery]);
  const doctors:      any[] = doctorsData?.doctors ?? [];

  function prevDay() { setSelectedDate(d => format(subDays(new Date(d), 1), 'yyyy-MM-dd')); setSearchQuery(''); }
  function nextDay() { setSelectedDate(d => format(addDays(new Date(d), 1), 'yyyy-MM-dd')); setSearchQuery(''); }
  function today()   { setSelectedDate(format(new Date(), 'yyyy-MM-dd')); setSearchQuery(''); }

  const stats = {
    total:     appointments.length,
    scheduled: appointments.filter(a => a.status === 'scheduled' || a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length,
    noShow:    appointments.filter(a => a.status === 'no_show').length,
  };

  return (
    <div className="p-6 max-w-screen-xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <Calendar size={22} className="text-orange" /> Appointments
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">Schedule and manage patient appointments</p>
        </div>
        <button
          onClick={() => setShowBook(true)}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-xl text-sm hover:bg-navy/90 transition"
        >
          <Plus size={16} /> Book Appointment
        </button>
      </div>

      {/* Date nav + stats */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={prevDay} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition">
            <ChevronLeft size={16} />
          </button>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm font-medium text-navy focus:outline-none focus:border-navy"
          />
          <button onClick={nextDay} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition">
            <ChevronRight size={16} />
          </button>
          <button onClick={today} className="px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition">
            Today
          </button>
        </div>

        <div className="flex gap-3 text-sm ml-2">
          <span className="text-gray-500">Total: <strong className="text-navy">{stats.total}</strong></span>
          <span className="text-blue-600">Scheduled: <strong>{stats.scheduled}</strong></span>
          <span className="text-green-600">Done: <strong>{stats.completed}</strong></span>
          {stats.noShow > 0 && <span className="text-orange-600">No-show: <strong>{stats.noShow}</strong></span>}
        </div>
      </div>

      {/* Filters + Search */}
      <div className="flex gap-3 flex-wrap items-center">
        <select
          value={doctorFilter}
          onChange={e => setDoctorFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
        >
          <option value="">All Doctors</option>
          {doctors.map((d: any) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
        >
          <option value="">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="confirmed">Confirmed</option>
          <option value="arrived">Arrived</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="no_show">No Show</option>
        </select>
        <ListSearchInput
          placeholder="Search by name or UHID…"
          value={searchQuery}
          onChange={setSearchQuery}
          className="w-64"
        />
        {searchQuery && (
          <span className="text-xs text-gray-400">
            {filteredAppointments.length} of {appointments.length} shown
          </span>
        )}
      </div>

      {/* Appointment list */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 size={22} className="animate-spin mr-2" /> Loading...
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Calendar size={36} className="mx-auto mb-3 opacity-25" />
            <p className="text-sm font-medium text-navy">No appointments on {format(new Date(selectedDate), 'dd MMM yyyy')}</p>
            <button onClick={() => setShowBook(true)}
              className="mt-3 text-xs text-navy hover:underline flex items-center gap-1 mx-auto">
              <Plus size={11} /> Book first appointment
            </button>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="text-center py-14 text-gray-400">
            <p className="text-sm">No patients match <strong className="text-navy">{searchQuery}</strong></p>
            <button onClick={() => setSearchQuery('')} className="mt-2 text-xs text-navy hover:underline">
              Clear search
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filteredAppointments.map((appt: any) => (
              <div key={appt.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/40 transition">
                {/* Time */}
                <div className="flex-none w-16 text-center">
                  <div className="text-sm font-bold text-navy">
                    {appt.slot_start?.slice(0, 5)}
                  </div>
                  <div className="text-xs text-gray-400">{appt.slot_end?.slice(0, 5)}</div>
                </div>

                {/* Patient */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setQuickViewId(appt.patient_id)}
                      className="font-medium text-gray-800 hover:text-navy hover:underline truncate text-left"
                    >
                      {appt.patient_name ?? appt.patient_uid}
                    </button>
                    {appt.patient_uid && (
                      <code className="text-2xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono select-all">
                        {appt.patient_uid}
                      </code>
                    )}
                    {appt.appointment_no && (
                      <code className="text-2xs text-navy/70 bg-navy/5 border border-navy/10 px-1.5 py-0.5 rounded font-mono select-all">
                        {appt.appointment_no}
                      </code>
                    )}
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                      appt.appointment_type === 'new' ? 'bg-blue-50 text-blue-600' :
                      appt.appointment_type === 'follow_up' ? 'bg-purple-50 text-purple-600' :
                      'bg-orange-50 text-orange-600')}>
                      {APPT_TYPE_LABEL[appt.appointment_type] ?? appt.appointment_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><User size={11} /> Dr. {appt.doctor_name}</span>
                    {appt.specialization && <span>{appt.specialization}</span>}
                    {appt.notes && <span className="truncate max-w-[200px]">{appt.notes}</span>}
                  </div>
                </div>

                {/* Status */}
                <div className="flex-none">
                  <span className={clsx('px-2 py-1 rounded-lg text-xs font-medium', STATUS_STYLE[appt.status] ?? 'bg-gray-100')}>
                    {appt.status?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex-none flex gap-1.5">
                  {appt.status === 'scheduled' && (
                    <>
                      <button
                        onClick={() => updateStatusMutation.mutate({ id: appt.id, status: 'confirmed' })}
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => updateStatusMutation.mutate({ id: appt.id, status: 'cancelled' })}
                        className="px-2 py-1 text-xs bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {(appt.status === 'confirmed' || appt.status === 'scheduled') && (
                    <button
                      onClick={() => checkInMutation.mutate(appt.id)}
                      disabled={checkInMutation.isPending}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition disabled:opacity-50"
                    >
                      {checkInMutation.isPending
                        ? <Loader2 size={11} className="animate-spin"/>
                        : <UserCheck size={11}/>
                      }
                      Check In
                    </button>
                  )}
                  {(appt.status === 'scheduled' || appt.status === 'confirmed') && (
                    <button
                      onClick={() => updateStatusMutation.mutate({ id: appt.id, status: 'no_show' })}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 transition"
                    >
                      No Show
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showBook && (
        <BookModal
          onClose={() => setShowBook(false)}
          onWalkinSuccess={({ appointment_no, token_number, encounter_no }) =>
            showToast(
              `Walk-in checked in — ${appointment_no} · Token T-${String(token_number).padStart(2, '0')} · ${encounter_no}`,
              'success',
            )
          }
        />
      )}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)}/>}
      <PatientQuickViewModal
        patientId={quickViewId}
        isOpen={!!quickViewId}
        onClose={() => setQuickViewId(null)}
      />
    </div>
  );
}
